import React, { useState } from "react";
import emailjs from "@emailjs/browser";

const SERVICE_ID = "YOUR_EMAILJS_SERVICE_ID";
const TEMPLATE_ID = "YOUR_EMAILJS_TEMPLATE_ID";
const PUBLIC_KEY = "YOUR_EMAILJS_PUBLIC_KEY";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  checkIn: "",
  checkOut: "",
  guests: "1",
  message: "",
};

function InputWrapper({ label, required, children, error }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1 mt-0.5">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
}

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Full name is required.";
  if (!form.email.trim()) {
    errors.email = "Email address is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Please enter a valid email address.";
  }
  if (!form.phone.trim()) {
    errors.phone = "Phone number is required.";
  } else if (!/^\+?[\d\s\-().]{7,20}$/.test(form.phone)) {
    errors.phone = "Please enter a valid phone number.";
  }
  if (!form.checkIn) errors.checkIn = "Check-in date is required.";
  if (!form.checkOut) {
    errors.checkOut = "Check-out date is required.";
  } else if (form.checkIn && form.checkOut && form.checkOut <= form.checkIn) {
    errors.checkOut = "Check-out must be after check-in.";
  }
  if (!form.guests || Number(form.guests) < 1) {
    errors.guests = "At least 1 guest is required.";
  }
  return errors;
}

export default function BookingForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"
  const [touched, setTouched] = useState({});

  const today = new Date().toISOString().split("T")[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const newErrors = validate({ ...form, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: newErrors[name] }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const newErrors = validate(form);
    setErrors((prev) => ({ ...prev, [name]: newErrors[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = Object.keys(initialForm).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);

    const validationErrors = validate(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setStatus("loading");

    const templateParams = {
      from_name: form.name,
      from_email: form.email,
      phone: form.phone,
      check_in: form.checkIn,
      check_out: form.checkOut,
      guests: form.guests,
      message: form.message || "No additional message.",
      reply_to: form.email,
    };

    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
      setStatus("success");
      setForm(initialForm);
      setTouched({});
      setErrors({});
    } catch (err) {
      console.error("EmailJS error:", err);
      setStatus("error");
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all duration-200 bg-gray-50 text-sm ${
      errors[field] ? "border-red-400 bg-red-50" : "border-gray-200"
    }`;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="section-subtitle mb-3">Reservations</p>
          <h2 className="section-title mb-4">Booking Inquiry</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Fill out the form below and we'll confirm your reservation within 24 hours. We can't wait to host you!
          </p>
        </div>

        {/* Success Banner */}
        {status === "success" && (
          <div className="mb-8 p-5 bg-green-50 border border-green-200 rounded-2xl flex items-start gap-4 animate-fadeUp">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-green-800 text-base mb-1">Inquiry Sent Successfully!</h3>
              <p className="text-green-700 text-sm leading-relaxed">
                Thank you for reaching out. We've received your booking inquiry and will get back to you within 24 hours to confirm your stay at Mariner Lodge.
              </p>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {status === "error" && (
          <div className="mb-8 p-5 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-4 animate-fadeUp">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-red-800 text-base mb-1">Something went wrong</h3>
              <p className="text-red-700 text-sm leading-relaxed">
                We couldn't send your inquiry. Please try again, or contact us directly at{" "}
                <a href="mailto:info@marinerlodge.pk" className="underline font-medium">
                  info@marinerlodge.pk
                </a>
                .
              </p>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-gray-100/80 p-8 md:p-10">
          <form onSubmit={handleSubmit} noValidate className="space-y-6">
            {/* Row 1 – Name & Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputWrapper label="Full Name" required error={errors.name}>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Ali Hassan"
                  className={inputClass("name")}
                  autoComplete="name"
                />
              </InputWrapper>
              <InputWrapper label="Email Address" required error={errors.email}>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="ali@example.com"
                  className={inputClass("email")}
                  autoComplete="email"
                />
              </InputWrapper>
            </div>

            {/* Row 2 – Phone & Guests */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputWrapper label="Phone Number" required error={errors.phone}>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="+92 300 123 4567"
                  className={inputClass("phone")}
                  autoComplete="tel"
                />
              </InputWrapper>
              <InputWrapper label="Number of Guests" required error={errors.guests}>
                <select
                  name="guests"
                  value={form.guests}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={inputClass("guests")}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? "Guest" : "Guests"}
                    </option>
                  ))}
                  <option value="9">9+ Guests (Group)</option>
                </select>
              </InputWrapper>
            </div>

            {/* Row 3 – Check-in & Check-out */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <InputWrapper label="Check-in Date" required error={errors.checkIn}>
                <input
                  type="date"
                  name="checkIn"
                  value={form.checkIn}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min={today}
                  className={inputClass("checkIn")}
                />
              </InputWrapper>
              <InputWrapper label="Check-out Date" required error={errors.checkOut}>
                <input
                  type="date"
                  name="checkOut"
                  value={form.checkOut}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  min={form.checkIn || today}
                  className={inputClass("checkOut")}
                />
              </InputWrapper>
            </div>

            {/* Stay duration hint */}
            {form.checkIn && form.checkOut && form.checkOut > form.checkIn && (
              <div className="flex items-center gap-2 text-sm text-ocean-700 bg-ocean-50 px-4 py-2.5 rounded-xl border border-ocean-100">
                <svg className="w-4 h-4 flex-shrink-0 text-ocean-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>
                  <span className="font-semibold">
                    {Math.round(
                      (new Date(form.checkOut) - new Date(form.checkIn)) /
                        (1000 * 60 * 60 * 24)
                    )}{" "}
                    night(s)
                  </span>{" "}
                  stay selected
                </span>
              </div>
            )}

            {/* Message */}
            <InputWrapper label="Additional Message" error={errors.message}>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={4}
                placeholder="Tell us about your trip — room preference, special requests, arrival time, or any questions..."
                className={`${inputClass("message")} resize-none`}
              />
            </InputWrapper>

            {/* Info note */}
            <div className="flex items-start gap-3 text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>
                This is a booking <strong>inquiry</strong>. Your reservation will be confirmed after we review availability and respond via email within 24 hours. No payment is required at this stage.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full flex items-center justify-center gap-2.5 bg-brand text-white font-semibold text-base px-8 py-4 rounded-2xl shadow-lg hover:bg-indigo-700 transition-all duration-300 hover:shadow-indigo-300 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg"
            >
              {status === "loading" ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending Inquiry…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send Booking Inquiry
                </>
              )}
            </button>
          </form>
        </div>

        {/* Alternative contact */}
        <div className="mt-10 text-center">
          <p className="text-gray-400 text-sm mb-4">Prefer to reach us directly?</p>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a
              href="tel:+923001234567"
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-brand transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-ocean-50 flex items-center justify-center border border-ocean-100">
                <svg className="w-4 h-4 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </span>
              +92 300 123 4567
            </a>
            <a
              href="mailto:info@marinerlodge.pk"
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-brand transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-ocean-50 flex items-center justify-center border border-ocean-100">
                <svg className="w-4 h-4 text-ocean-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </span>
              info@marinerlodge.pk
            </a>
            <a
              href="https://wa.me/923001234567"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-brand transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-ocean-50 flex items-center justify-center border border-ocean-100">
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </span>
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

